import {
  Avatar,
  Button,
  Icon,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Roles } from 'app/auth';
import { logoutUser } from 'app/auth/store/userSlice';
import { useEventTracker } from 'app/hooks';
import { TAB_SELECTED_EVENTS } from 'app/tracking';
import navigateToConnectV1 from 'app/utils/navigateToConnectV1';
import { DEPLOYMENT_REGIONS } from 'app/constants';
import config from '@/config.json';

const rolesMap = {
  [Roles.PHARMACY_ADMIN]: 'Group Admin',
  [Roles.PHARMACIST]: 'Pharmacist',
  [Roles.SUPPORT]: 'Support',
  [Roles.PRESCRIBER]: 'Prescriber',
};

function UserMenu() {
  const dispatch = useDispatch();
  const user = useSelector(({ auth }) => auth.user);
  const trackEvent = useEventTracker();

  const [userMenu, setUserMenu] = useState(null);

  const userMenuClick = (event) => {
    setUserMenu(event.currentTarget);
  };

  const userMenuClose = () => {
    setUserMenu(null);
  };

  const handleMyProfileClick = () => {
    trackEvent(TAB_SELECTED_EVENTS.MY_PROFILE.action, TAB_SELECTED_EVENTS.MY_PROFILE);
    userMenuClose();
    const userId = user?._id || user?.id;

    if (!userId) {
      throw new Error('User ID is required to navigate to My Profile');
    }

    navigateToConnectV1(`/users/${userId}`);
  };

  return (
    <>
      <Button
        className="min-h-40 min-w-40 px-0 md:px-16 py-0 md:py-6"
        onClick={userMenuClick}
        color="inherit"
      >
        <div className="hidden md:flex flex-col mx-4 items-end">
          <Typography component="span" className="font-semibold flex">
            {`${user.forename} ${user.surname}`}
          </Typography>
          <Typography className="text-11 font-medium capitalize" color="textSecondary">
            {user.roles && user.roles.length > 0
              ? user.roles
                  .map((role) => rolesMap[role])
                  .filter(Boolean)
                  .join('/')
              : 'Guest'}
          </Typography>
        </div>

        {user.photoURL ? (
          <Avatar className="md:mx-4" alt="user photo" src={user.photoURL} />
        ) : (
          <Avatar className="md:mx-4">{user.forename[0]}</Avatar>
        )}
      </Button>

      <Popover
        open={Boolean(userMenu)}
        anchorEl={userMenu}
        onClose={userMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        classes={{
          paper: 'py-8',
        }}
      >
        {!user.roles || user.roles.length === 0 ? (
          <>
            <MenuItem component={Link} to="/login" role="button">
              <ListItemIcon className="min-w-40">
                <Icon>lock</Icon>
              </ListItemIcon>
              <ListItemText primary="Login" />
            </MenuItem>
            <MenuItem component={Link} to="/register" role="button">
              <ListItemIcon className="min-w-40">
                <Icon>person_add</Icon>
              </ListItemIcon>
              <ListItemText primary="Register" />
            </MenuItem>
          </>
        ) : (
          <>
            {config.deploymentRegion === DEPLOYMENT_REGIONS.UK && (
              <MenuItem onClick={handleMyProfileClick} role="button">
                <ListItemIcon className="min-w-40">
                  <Icon>account_circle</Icon>
                </ListItemIcon>
                <ListItemText primary="My Profile" />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                dispatch(logoutUser());
                userMenuClose();
              }}
            >
              <ListItemIcon className="min-w-40">
                <Icon>exit_to_app</Icon>
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </>
        )}
      </Popover>
    </>
  );
}

export default UserMenu;
